import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, PayloadError, RecordSourceProxy} from 'relay-runtime'

import {RELATES_TO_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsRelatesToListView'
import type {
  RemoveRelatesToInput,
  removeRelatesToMutation,
  removeRelatesToMutation$data,
} from './__generated__/removeRelatesToMutation.graphql'

function optimisticRemoveRelatesTo(store: RecordSourceProxy, input: RemoveRelatesToInput) {
  // Only the viewer's issue is reliably in the store; the server response reconciles the symmetric reverse edge.
  const issueRecord = store.get(input.issueId)
  if (!issueRecord) return

  const connection = issueRecord.getLinkedRecord('relatesTo', {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    const filteredNodes = existingNodes.filter(node => node?.getDataID() !== input.relatedIssueId)
    if (filteredNodes.length !== existingNodes.length) {
      connection.setLinkedRecords(filteredNodes, 'nodes')
    }
    // A removal always drops the total by one (even for a target beyond the visible top 3); the server reconciles.
    const currentTotal = (connection.getValue('totalCount') as number) ?? 0
    connection.setValue(Math.max(0, currentTotal - 1), 'totalCount')
  }
}

export function removeRelatesToMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimistic = true,
}: {
  environment: Environment
  input: RemoveRelatesToInput
  onError?: (error: Error) => void
  onCompleted?: (response: removeRelatesToMutation$data, errors?: readonly PayloadError[] | null) => void
  optimistic?: boolean
}) {
  return commitMutation<removeRelatesToMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation removeRelatesToMutation($input: RemoveRelatesToInput!) @raw_response_type {
        removeRelatesTo(input: $input) {
          issue {
            id
            topRelatesTo: relatesTo(first: 3, ranked: true) {
              totalCount
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
          }
          relatedIssue {
            id
            topRelatesTo: relatesTo(first: 3, ranked: true) {
              totalCount
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: optimistic ? store => optimisticRemoveRelatesTo(store, input) : undefined,
    onError: error => onError && onError(error),
    onCompleted: (response, errors) => onCompleted && onCompleted(response, errors),
    updater: store => {
      // Invalidate the picker's id set and the paginated "view all" connection on both symmetric endpoints.
      store.get(input.issueId)?.getLinkedRecord('relatesTo', {first: 100})?.invalidateRecord()
      store.get(input.relatedIssueId)?.getLinkedRecord('relatesTo', {first: 100})?.invalidateRecord()
      store
        .get(ConnectionHandler.getConnectionID(input.issueId, RELATES_TO_LIST_ALL_RELAY_CONNECTION))
        ?.invalidateRecord()
      store
        .get(ConnectionHandler.getConnectionID(input.relatedIssueId, RELATES_TO_LIST_ALL_RELAY_CONNECTION))
        ?.invalidateRecord()
    },
  })
}
